/**
 * Core-100 R4 wave 3 (X4) — CORE-AE GAP-005: "Independent tab loading produces unsignalled divergence."
 *
 * Each Command Center tab loads on its own, so two tabs can show the ticket as of different moments and nothing
 * said so. This stamp says, for the tab in view, WHEN its data was read, and — when the ticket has changed since
 * (the board's live copy of the ticket carries the server's `updated_at`) — that the tab may be out of date, with a
 * Refresh that re-reads it.
 *
 * "When it was read" is honest: it is the OLDEST `dataUpdatedAt` among the ticket-scoped queries that the tab has on
 * screen right now (a cached answer counts from when it was fetched, not from when the tab opened); a tab whose
 * readers do not use the query cache counts from the moment it mounted (they read on mount).
 */
import { useState, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Clock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

/** Oldest fetch time among the active (on-screen, enabled) queries whose key names this ticket; null when none. */
export function oldestObservedReadAt(queryClient: QueryClient, ticketId: string): number | null {
  let oldest: number | null = null;
  for (const q of queryClient.getQueryCache().getAll()) {
    if (!q.isActive()) continue; // on screen AND enabled (a disabled reader is not showing a read)
    if (!q.queryKey.some((k) => k === ticketId)) continue;
    const at = q.state.dataUpdatedAt;
    if (!at) continue;
    if (oldest === null || at < oldest) oldest = at;
  }
  return oldest;
}

/** Pure: is a tab read at `readAt` stale against the ticket's last change? (unknown change time = not claimed stale) */
export function isSectionStale(readAtMs: number, ticketUpdatedAt: string | null | undefined): boolean {
  if (!ticketUpdatedAt) return false;
  const changed = Date.parse(ticketUpdatedAt);
  return Number.isFinite(changed) && changed > readAtMs;
}

export function CommandCenterFreshness({
  ticketId, section, ticketUpdatedAt, timeZone, onRefresh,
}: {
  ticketId: string;
  section: string;
  ticketUpdatedAt: string | null | undefined;
  timeZone?: string;
  onRefresh: () => void;
}) {
  const queryClient = useQueryClient();
  const [mountedAt] = useState(() => Date.now());
  const cacheReadAt = useSyncExternalStore(
    (cb) => queryClient.getQueryCache().subscribe(cb),
    () => oldestObservedReadAt(queryClient, ticketId),
    () => null,
  );
  const readAt = cacheReadAt ?? mountedAt;
  const stale = isSectionStale(readAt, ticketUpdatedAt);
  const fmt = (ms: number) => new Date(ms).toLocaleTimeString([], {
    hour: 'numeric', minute: '2-digit', second: '2-digit', ...(timeZone ? { timeZone } : {}),
  });

  return (
    <div
      role="status"
      data-testid="command-center-freshness"
      data-section={section}
      data-stale={stale ? 'true' : 'false'}
      data-read-at={new Date(readAt).toISOString()}
      data-ticket-changed-at={ticketUpdatedAt ?? ''}
      className={`mx-6 mb-2 flex flex-wrap items-center gap-2 rounded-md px-3 py-1.5 text-xs ${stale
        ? 'border border-amber-500/40 bg-amber-500/10' : 'text-muted-foreground'}`}
    >
      <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>
        {section} read at {fmt(readAt)}.
        {stale && <> The ticket changed after that — this tab may be out of date.</>}
      </span>
      {stale && (
        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={onRefresh}
          data-testid="command-center-freshness-refresh">
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh
        </Button>
      )}
    </div>
  );
}
